import { AutocompleteInput } from "@/components/ui/AutocompleteInput";
import { Select } from "@/components/ui/Select";
import React, { useState } from "react";

import { DateInput } from "../../ui/DateInput";
import { VisitCitationRowsEditor } from "./VisitCitationRowsEditor";
import type { VisitHeaderSaveValues } from "./VisitHeaderForm.types";
import type {
  CaseFileOption,
  EditingHeaderValues,
  MedChronTemplate,
  VisitDisplay,
  VisitFieldSuggestions,
} from "./types";
import { buildPageLimits, validateCitationDraftRows } from "./utils/visitCitations";
import {
  buildFileOptions,
  buildInitialHeaderValues,
  buildVisitTypeOptions,
} from "./utils/visitFormatting";

interface VisitHeaderFormProps {
  visit: VisitDisplay;
  availableFiles: CaseFileOption[];
  fileMapping: Record<string, string>;
  fieldSuggestions: VisitFieldSuggestions;
  template: MedChronTemplate | null;
  onSave: (visitId: string, values: VisitHeaderSaveValues) => void;
  onCancel: () => void;
  hasVisitTypeError: boolean;
  onClearVisitTypeError: () => void;
}

const VISIT_TYPE_ERROR_ID = "visit-type-error";

export const VisitHeaderForm: React.FC<VisitHeaderFormProps> = ({
  visit,
  availableFiles,
  fileMapping,
  fieldSuggestions,
  template,
  onSave,
  onCancel,
  hasVisitTypeError,
  onClearVisitTypeError,
}) => {
  // Kept local (not lifted to an ancestor) so keystrokes never cross the
  // react-virtuoso item boundary, which otherwise causes double commits per
  // keystroke and breaks cursor position / native undo (see react-virtuoso
  // issue 1115 upstream).
  const [values, setValues] = useState<EditingHeaderValues>(() => buildInitialHeaderValues(visit));
  const [initialCitationRows] = useState(() => new Set(values.citations));
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Files the visit already cites stay valid even when no longer in the case file list.
  const validFileIds = new Set([
    ...availableFiles.map((file) => file.file_id),
    ...visit.citations.map((citation) => citation.file_id),
  ]);
  const citationValidation = validateCitationDraftRows(
    values.citations,
    validFileIds,
    buildPageLimits(availableFiles),
    initialCitationRows
  );

  const handleSave = () => {
    if (!citationValidation.isValid) {
      setHasAttemptedSave(true);
      return;
    }
    onSave(visit.visit_id, {
      visit_date: values.visit_date,
      visit_type_id: values.visit_type_id,
      provider_name: values.provider_name,
      facility_name: values.facility_name,
      visit_subtype: values.visit_subtype,
      citations: citationValidation.citations,
    });
  };

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <DateInput
        value={values.visit_date}
        onChange={(val) => setValues((v) => ({ ...v, visit_date: val }))}
        className="w-full cursor-pointer rounded-md border border-neutral-300 px-3 py-2 pr-10 text-sm"
      />
      <Select
        value={values.visit_type_id}
        onChange={(next) => {
          setValues((v) => ({ ...v, visit_type_id: next }));
          if (next) {
            onClearVisitTypeError();
          }
        }}
        options={buildVisitTypeOptions(template)}
        placeholder="Select visit type"
        ariaLabel="Visit type"
        ariaDescribedBy={hasVisitTypeError ? VISIT_TYPE_ERROR_ID : undefined}
        ariaInvalid={hasVisitTypeError || undefined}
        className={`w-full rounded-md border px-3 py-2 text-sm transition-all ${
          hasVisitTypeError
            ? "border-destructive bg-destructive-subtle focus-visible:shadow-focus-danger"
            : "border-neutral-300"
        }`}
      />
      {hasVisitTypeError && (
        <p id={VISIT_TYPE_ERROR_ID} className="text-xs text-destructive-text">
          Please select a visit type
        </p>
      )}
      <AutocompleteInput
        placeholder="Subtype"
        ariaLabel="Subtype"
        value={values.visit_subtype}
        suggestions={fieldSuggestions.subtypes}
        onChange={(next) => setValues((v) => ({ ...v, visit_subtype: next }))}
        className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
      />
      <AutocompleteInput
        placeholder="Provider"
        ariaLabel="Provider"
        value={values.provider_name}
        suggestions={fieldSuggestions.providers}
        onChange={(next) => setValues((v) => ({ ...v, provider_name: next }))}
        className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
      />
      <AutocompleteInput
        placeholder="Facility"
        ariaLabel="Facility"
        value={values.facility_name}
        suggestions={fieldSuggestions.facilities}
        onChange={(next) => setValues((v) => ({ ...v, facility_name: next }))}
        className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
      />
      <VisitCitationRowsEditor
        rows={values.citations}
        rowErrors={hasAttemptedSave ? citationValidation.rowErrors : []}
        fileOptions={buildFileOptions(availableFiles, fileMapping)}
        onChange={(citations) => setValues((v) => ({ ...v, citations }))}
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-neutral-300 bg-card px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
